import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { API } from '../../App';
import TabNavigation from '../../components/TabNavigation';
import { useAlert } from '../../context/AlertContext';
import { formatErrorMessage } from '../../utils/errorFormatter';
import { departmentLabel } from '../../utils/departmentLabel';
import { useSiteRequired } from '../../components/SiteRequiredGate';
import { usePermissions } from '../../context/PermissionContext';
import { useSite } from '../../context/SiteContext';
import { CURRENCIES, getTenantCurrency } from '../../utils/regional';

const TrainingCourses = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermission } = usePermissions();
  const { showSuccess, showError, showWarning, confirm } = useAlert();
  const { requireSite } = useSiteRequired();
  const { activeSiteId, activeSiteName } = useSite();
  const [courses, setCourses] = useState([]);
  const [filteredCourses, setFilteredCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null);
  const [filterType, setFilterType] = useState('All');
  // QA #101: department filter. Filters on the stored department NAME, not the
  // id — the name is what the card renders (QA #99), so the two can never
  // disagree, and legacy rows that carry a name with a NULL department_id stay
  // filterable. Same approach as HIRAList's departmentLabel().
  const [filterDepartment, setFilterDepartment] = useState('All');

  const [courseForm, setCourseForm] = useState({
    course_title: '',
    course_code: '',
    training_type: 'Mandatory',
    description: '',
    trainer_institution: '',
    duration_hours: '',
    validity_period_months: '',
    has_quiz: false,
    passing_score: '',
    evidence_required: false,
    cost: '',
    // REG-05: the tenant's own currency, never a hardcoded INR. Empty when the
    // tenant has none configured — the user must then pick one.
    currency: getTenantCurrency(),
    department_id: ''
  });
  const [departments, setDepartments] = useState([]);

  useEffect(() => {
    fetchCourses();
    const token = localStorage.getItem('token');
    axios.get(`${API}/departments`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setDepartments(res.data.departments || []))
      .catch(() => setDepartments([]));
  }, []);

  // Auto-open edit modal when navigated with ?edit=ID from detail page
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const editId = params.get('edit');
    if (editId && courses.length > 0) {
      const courseToEdit = courses.find(c => c.id === editId);
      if (courseToEdit) {
        openModal(courseToEdit);
        navigate('/training/courses', { replace: true });
      }
    }
  }, [courses, location.search]);

  useEffect(() => {
    setFilteredCourses(
      courses.filter(c =>
        (filterType === 'All' || c.training_type === filterType)
        && (filterDepartment === 'All' || departmentLabel(c) === filterDepartment)
      )
    );
  }, [filterType, filterDepartment, courses]);

  // Options come from the loaded rows, not the departments master, so a course
  // filed under a department that has since been deleted remains findable
  // (QA #102's concern). "Not Specified" only appears when some course actually
  // lacks a department.
  const departmentFilterOptions = [
    ...new Set(courses.map(departmentLabel))
  ].sort();

  const fetchCourses = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/training-courses`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCourses(response.data);
      setFilteredCourses(response.data);
    } catch (err) {
      showError(formatErrorMessage(err, 'Failed to fetch courses'));
    } finally {
      setLoading(false);
    }
  };

  const openModal = (course = null) => {
    // QA #97: a new course is site-scoped, so require the site BEFORE the form
    // opens. Previously the whole form could be filled in and the create then
    // failed on the server (routes/training.py raises "No active site
    // selected..."), losing every entered value. Create is gated; EDIT is not,
    // because an existing course already carries its own site.
    if (!course && !requireSite(showWarning, 'training course')) return;
    if (course) {
      setEditingCourse(course);
      setCourseForm({
        course_title: course.course_title,
        course_code: course.course_code || '',
        training_type: course.training_type,
        description: course.description || '',
        trainer_institution: course.trainer_institution || '',
        duration_hours: course.duration_hours || '',
        validity_period_months: course.validity_period_months || '',
        has_quiz: course.has_quiz || false,
        passing_score: course.passing_score || '',
        evidence_required: course.evidence_required || false,
        cost: course.cost || '',
        currency: course.currency || getTenantCurrency(),
        department_id: course.department_id || ''
      });
    } else {
      setEditingCourse(null);
      setCourseForm({
        course_title: '',
        course_code: '',
        training_type: 'Mandatory',
        description: '',
        trainer_institution: '',
        duration_hours: '',
        validity_period_months: '',
        has_quiz: false,
        passing_score: '',
        evidence_required: false,
        cost: '',
        currency: getTenantCurrency(),
        department_id: ''
      });
    }
    setShowModal(true);
  };

  const handleSave = async () => {
    // REG-05: the server refuses a course without a currency rather than
    // silently writing INR; say so before the round trip.
    if (!courseForm.currency) {
      showError('Please select a currency for this course');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const data = {
        ...courseForm,
        duration_hours: courseForm.duration_hours ? parseFloat(courseForm.duration_hours) : null,
        validity_period_months: courseForm.validity_period_months ? parseInt(courseForm.validity_period_months) : null,
        passing_score: courseForm.passing_score ? parseInt(courseForm.passing_score) : null,
        cost: courseForm.cost ? parseFloat(courseForm.cost) : null,
        site_id: activeSiteId || undefined,
        site_name: activeSiteName || undefined,
      };

      if (editingCourse) {
        await axios.put(
          `${API}/training-courses/${editingCourse.id}`,
          data,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        showSuccess('Training course updated successfully');
      } else {
        await axios.post(
          `${API}/training-courses`,
          data,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        showSuccess('Training course created successfully');
      }

      setShowModal(false);
      fetchCourses();
    } catch (err) {
      showError(formatErrorMessage(err, 'Failed to save course'));
    }
  };

  const handleDelete = async (courseId) => {
    const confirmed = await confirm(
      'Delete Training Course',
      'Are you sure you want to delete this training course? This action cannot be undone.',
      { destructive: true, confirmLabel: 'Delete' }
    );

    if (!confirmed) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/training-courses/${courseId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showSuccess('Training course deleted successfully');
      fetchCourses();
    } catch (err) {
      showError(formatErrorMessage(err, 'Failed to delete course'));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <TabNavigation user={user} onLogout={onLogout} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
        <TabNavigation user={user} onLogout={onLogout} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="p-6">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/training')}
            className="text-blue-600 hover:underline mb-4 flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Training Dashboard
          </button>
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-3xl font-bold text-gray-800">📖 Training Courses</h1>
              <p className="text-gray-600 mt-1">{filteredCourses.length} course(s) found</p>
            </div>
            {hasPermission('training', 'create') && (
              <button
                onClick={() => openModal()}
                className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                New Course
              </button>
            )}
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-md p-4 mb-6">
          <div className="flex gap-2">
            {['All', 'Mandatory', 'Job-Specific', 'Refresher', 'Contractor Training'].map(type => (
              <button
                key={type}
                onClick={() => setFilterType(type)}
                className={`px-4 py-2 rounded-lg font-semibold ${
                  filterType === type
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {type}
              </button>
            ))}
            {/* QA #101 */}
            <select
              value={filterDepartment}
              onChange={(e) => setFilterDepartment(e.target.value)}
              className="ml-auto px-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 font-semibold"
            >
              <option value="All">All Departments</option>
              {departmentFilterOptions.map(d => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Courses Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredCourses.map(course => (
            <div key={course.id} className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate(`/training/courses/${course.id}`)}>
              <div className="flex justify-between items-start mb-3">
                <div>
                  <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                    course.training_type === 'Mandatory' ? 'bg-red-100 text-red-700' :
                    course.training_type === 'Job-Specific' ? 'bg-blue-100 text-blue-700' :
                    course.training_type === 'Refresher' ? 'bg-green-100 text-green-700' :
                    'bg-purple-100 text-purple-700'
                  }`}>
                    {course.training_type}
                  </span>
                </div>
                {/* QA #98: Edit and Delete now live on the Read/View page, matching
                    the pattern used elsewhere in the app. The card itself already
                    navigates to the detail page (onClick above), so clicking it
                    opens Read as QA asked. */}
              </div>

              <h3 className="font-bold text-lg text-gray-800 mb-2">{course.course_title}</h3>
              {course.course_code && (
                <p className="text-sm text-gray-500 mb-2">Code: {course.course_code}</p>
              )}
              {course.description && (
                <p className="text-sm text-gray-600 mb-3 line-clamp-2">{course.description}</p>
              )}

              <div className="space-y-1 text-sm text-gray-600">
                {/* QA #99 */}
                <p>🏛️ {departmentLabel(course)}</p>
                {course.trainer_institution && (
                  <p>🏢 {course.trainer_institution}</p>
                )}
                {course.duration_hours && (
                  <p>⏱️ {course.duration_hours} hours</p>
                )}
                {course.validity_period_months && (
                  <p>📅 Valid for {course.validity_period_months} months</p>
                )}
                {course.has_quiz && (
                  <p>✅ Has Quiz (Pass: {course.passing_score}%)</p>
                )}
                {course.evidence_required && (
                  <p>📄 Evidence Required</p>
                )}
              </div>
            </div>
          ))}
        </div>

        {filteredCourses.length === 0 && (
          <div className="text-center py-12 bg-gray-50 rounded-lg">
            <p className="text-gray-600">No training courses found</p>
            {hasPermission('training', 'create') && (
              <button
                onClick={() => openModal()}
                className="mt-4 text-blue-600 hover:underline font-semibold"
              >
                Create your first course
              </button>
            )}
          </div>
        )}

        {/* Course Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
                <h2 className="text-2xl font-bold text-gray-800">
                  {editingCourse ? 'Edit Course' : 'New Training Course'}
                </h2>
                <button onClick={() => setShowModal(false)} className="text-gray-500 hover:text-gray-700">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Course Title <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={courseForm.course_title}
                      onChange={(e) => setCourseForm({ ...courseForm, course_title: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Course Code</label>
                    <input
                      type="text"
                      value={courseForm.course_code}
                      onChange={(e) => setCourseForm({ ...courseForm, course_code: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Training Type</label>
                    <select
                      value={courseForm.training_type}
                      onChange={(e) => setCourseForm({ ...courseForm, training_type: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    >
                      <option value="Mandatory">Mandatory</option>
                      <option value="Job-Specific">Job-Specific</option>
                      <option value="Refresher">Refresher</option>
                      <option value="Contractor Training">Contractor Training</option>
                    </select>
                  </div>

                  <div className="col-span-2">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Description</label>
                    <textarea
                      value={courseForm.description}
                      onChange={(e) => setCourseForm({ ...courseForm, description: e.target.value })}
                      rows={3}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>

                  <div className="col-span-2">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Trainer/Institution</label>
                    <input
                      type="text"
                      value={courseForm.trainer_institution}
                      onChange={(e) => setCourseForm({ ...courseForm, trainer_institution: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>

                  <div className="col-span-2">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Department</label>
                    <select
                      value={courseForm.department_id}
                      onChange={(e) => setCourseForm({ ...courseForm, department_id: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    >
                      <option value="">— Select department —</option>
                      {departments.map((d) => (
                        <option key={d.id} value={d.id}>{d.name}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Duration (hours)</label>
                    <input
                      type="number"
                      step="0.5"
                      value={courseForm.duration_hours}
                      onChange={(e) => setCourseForm({ ...courseForm, duration_hours: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Validity (months)</label>
                    <input
                      type="number"
                      value={courseForm.validity_period_months}
                      onChange={(e) => setCourseForm({ ...courseForm, validity_period_months: e.target.value })}
                      placeholder="e.g., 12, 36"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Cost</label>
                    {/* min-w-0 is load-bearing, not decoration. A flex item
                        defaults to min-width:auto, so neither the currency
                        select nor the number input would shrink below its own
                        content — together they ran 56px past this grid cell and
                        sat on top of the Passing Score field next to it.
                        shrink-0 keeps the currency at its intended width and
                        lets the amount take whatever is left. */}
                    <div className="flex gap-2 min-w-0">
                      <select
                        value={courseForm.currency}
                        onChange={(e) => setCourseForm({ ...courseForm, currency: e.target.value })}
                        className="w-24 shrink-0 px-2 py-2 border border-gray-300 rounded-lg"
                      >
                        {!courseForm.currency && <option value="">Select</option>}
                        {CURRENCIES.map((c) => (
                          <option key={c.code} value={c.code}>{c.label}</option>
                        ))}
                        {/* A stored code outside the shared list (e.g. a legacy
                            row) must still display rather than silently flip. */}
                        {courseForm.currency
                          && !CURRENCIES.some((c) => c.code === courseForm.currency) && (
                          <option value={courseForm.currency}>{courseForm.currency}</option>
                        )}
                      </select>
                      <input
                        type="number"
                        step="0.01"
                        value={courseForm.cost}
                        onChange={(e) => setCourseForm({ ...courseForm, cost: e.target.value })}
                        className="flex-1 min-w-0 px-4 py-2 border border-gray-300 rounded-lg"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Passing Score (%) {courseForm.has_quiz && <span className="text-red-500">*</span>}
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="100"
                      value={courseForm.passing_score}
                      onChange={(e) => setCourseForm({ ...courseForm, passing_score: e.target.value })}
                      disabled={!courseForm.has_quiz}
                      className={`w-full px-4 py-2 border rounded-lg disabled:bg-gray-100 ${
                        courseForm.has_quiz && (!courseForm.passing_score || parseInt(courseForm.passing_score) <= 0)
                          ? 'border-red-500'
                          : 'border-gray-300'
                      }`}
                    />
                    {courseForm.has_quiz && (!courseForm.passing_score || parseInt(courseForm.passing_score) <= 0) && (
                      <p className="text-xs text-red-500 mt-1">Passing score is required when quiz is enabled</p>
                    )}
                  </div>

                  <div className="col-span-2 space-y-2">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={courseForm.has_quiz}
                        onChange={(e) => setCourseForm({ ...courseForm, has_quiz: e.target.checked, ...(!e.target.checked && { passing_score: '' }) })}
                        className="w-4 h-4"
                      />
                      <span className="text-sm font-semibold text-gray-700">Has Quiz</span>
                    </label>

                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={courseForm.evidence_required}
                        onChange={(e) => setCourseForm({ ...courseForm, evidence_required: e.target.checked })}
                        className="w-4 h-4"
                      />
                      <span className="text-sm font-semibold text-gray-700">Evidence Required</span>
                    </label>
                  </div>
                </div>
              </div>

              <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
                <button
                  onClick={() => setShowModal(false)}
                  className="px-6 py-2 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={!courseForm.course_title || (courseForm.has_quiz && (!courseForm.passing_score || parseInt(courseForm.passing_score) <= 0))}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400"
                >
                  {editingCourse ? 'Update Course' : 'Create Course'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
      </div>
  );
};

export default TrainingCourses;
